import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';

interface GovernanceStatus {
  mustAcknowledge: boolean;
  currentWording?: string;
}

interface GovernanceGateProps {
  children: React.ReactNode;
  userId: string | null;
}

export default function GovernanceGate({ children, userId }: GovernanceGateProps) {
  const [status, setStatus] = useState<GovernanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [ackChecked, setAckChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!userId) {
      setStatus(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await apiService.get<GovernanceStatus>(API_ENDPOINTS.USERS.GOVERNANCE_STATUS);
      setStatus(res.data);
    } catch {
      setStatus({ mustAcknowledge: false });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleAcknowledge = async () => {
    if (!ackChecked || submitting) return;
    try {
      setSubmitting(true);
      await apiService.post(API_ENDPOINTS.USERS.ACKNOWLEDGE_GOVERNANCE);
      await fetchStatus();
      setAckChecked(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !userId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {children}
      <Dialog
        open={!!(status?.mustAcknowledge)}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>Governance and Compliance</DialogTitle>
        <DialogContent>
          <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
            {status?.currentWording || 'No wording configured.'}
          </Typography>
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={ackChecked}
                  onChange={(e) => setAckChecked(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="I have read and confirm the above."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleAcknowledge}
            disabled={!ackChecked || submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
